import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StepperSelectionEvent } from '@angular/cdk/stepper';

import { PolicyTemplateService, policyNameUniqueValidator } from '../../../../services/entity-services/policy-template.service';
import { PolicyTemplateSummary } from '../../../../interfaces/policy-templates/policy-template-summary.interface';
import { CreatePolicyTemplateRequest } from '../../../../interfaces/policy-templates/create-policy-template-request.interface';
import { DuplicateCheckRequest } from '../../../../interfaces/policy-templates/duplicate-check.interface';
import {
  UaCleanupAction,
  UaPolicySteps,
  QuotaLimitPolicy,
  QuotaLimitAction,
  QuotaNotificationBand,
  NotificationConfig,
  NotificationChannel,
} from '../../../../interfaces/policy-templates/policy-cleanup-actions.interface';

@Component({
  selector: 'app-ua-policy-wizard',
  templateUrl: './ua-policy-wizard.component.html',
  styleUrls: ['./ua-policy-wizard.component.css']
})
export class UaPolicyWizardComponent implements OnInit {
  infoForm!: FormGroup;
  creationForm!: FormGroup;
  dailyCleanupForm!: FormGroup;
  classEndCleanupForm!: FormGroup;
  quotaLimitForm!: FormGroup;

  isDuplicateChecking = false;
  duplicatePolicy: PolicyTemplateSummary | null = null;
  isSubmitting = false;
  drawerPolicy: PolicyTemplateSummary | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly policyTemplateService: PolicyTemplateService,
  ) {}

  ngOnInit(): void {
    this.infoForm = this.fb.group({
      policyName: ['', {
        validators: [
          Validators.required,
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/)
        ],
        asyncValidators: [policyNameUniqueValidator(this.policyTemplateService, 'UserAccount')],
        updateOn: 'change'
      }],
      description: ['', [Validators.maxLength(500)]]
    });

    this.creationForm = this.fb.group({
      creationMode: ['on_demand', Validators.required]
    });

    this.dailyCleanupForm = this.fb.group({
      actionType: ['DoNothing', Validators.required]
    });

    this.classEndCleanupForm = this.fb.group({
      actionType: ['DoNothing', Validators.required]
    });

    // Quota: actionType 'DoNothing' = no quota configured.
    // Any other action requires thresholdAmount + 100% band.
    this.quotaLimitForm = this.fb.group({
      actionType: ['DoNothing'],
      thresholdAmount: [null],
      additionalAmount: [null],
      notificationBands: this.fb.array([])
    }, { validators: this.quotaGroupValidator() });
  }

  private quotaGroupValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const action = group.get('actionType')?.value;
      if (!action || action === 'DoNothing') return null;

      const errors: ValidationErrors = {};
      const threshold = group.get('thresholdAmount')?.value;
      if (!threshold || threshold <= 0) errors['thresholdRequired'] = true;

      if (action === 'ExtendQuota') {
        const addl = group.get('additionalAmount')?.value;
        if (!addl || addl <= 0) errors['additionalAmountRequired'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  get notificationBandsArray(): FormArray {
    return this.quotaLimitForm.get('notificationBands') as FormArray;
  }

  onStepChange(event: StepperSelectionEvent): void {
    if (event.selectedIndex === 5) {
      this.checkForDuplicate();
    }
  }

  checkForDuplicate(): void {
    this.isDuplicateChecking = true;
    this.duplicatePolicy = null;
    const request: DuplicateCheckRequest = {
      resourceType: 'UserAccount',
      uaSteps: this.buildUaSteps()
    };
    this.policyTemplateService.checkDuplicate(request).subscribe({
      next: (res) => {
        this.isDuplicateChecking = false;
        this.duplicatePolicy = res.isDuplicate ? res.existing : null;
      },
      error: () => { this.isDuplicateChecking = false; }
    });
  }

  private buildUaAction(form: FormGroup): UaCleanupAction {
    const type = form.get('actionType')!.value as string;
    if (type === 'DeactivateSubscription') return { actionType: 'DeactivateSubscription' };
    if (type === 'DeleteResources') return { actionType: 'DeleteResources' };
    return { actionType: 'DoNothing' };
  }

  private buildQuotaAction(): QuotaLimitAction {
    const type = this.quotaLimitForm.get('actionType')?.value as string;
    if (type === 'DeactivateSubscription') return { actionType: 'DeactivateSubscription' };
    if (type === 'DeleteResourcesAndSetReadOnly') return { actionType: 'DeleteResourcesAndSetReadOnly' };
    return { actionType: 'ExtendQuota', additionalAmount: this.quotaLimitForm.get('additionalAmount')!.value };
  }

  private buildNotificationBands(): QuotaNotificationBand[] {
    return (this.notificationBandsArray.controls as FormGroup[]).map(bandGroup => {
      const notifications = (bandGroup.get('notifications') as FormArray).controls as FormGroup[];
      return {
        triggerPercentage: bandGroup.get('triggerPercentage')!.value,
        notifications: notifications.map(row => ({
          teamId: row.get('teamId')!.value,
          teamName: row.get('teamName')!.value,
          userIds: row.get('userIds')!.value ?? [],
          channels: [
            ...(row.get('emailChannel')!.value ? ['Email' as NotificationChannel] : []),
            ...(row.get('appChannel')!.value ? ['App' as NotificationChannel] : [])
          ]
        } satisfies NotificationConfig))
      };
    });
  }

  private buildUaSteps(): UaPolicySteps {
    const action = this.quotaLimitForm.get('actionType')?.value;
    const quotaLimit: QuotaLimitPolicy | undefined = (action && action !== 'DoNothing')
      ? {
          thresholdAmount: this.quotaLimitForm.get('thresholdAmount')!.value,
          action: this.buildQuotaAction(),
          notificationBands: this.buildNotificationBands()
        }
      : undefined;

    return {
      creationMode: 'on_demand',
      dailyCleanup: this.buildUaAction(this.dailyCleanupForm),
      classEndCleanup: this.buildUaAction(this.classEndCleanupForm),
      quotaLimit
    };
  }

  formatUaCleanupAction(form: FormGroup): string {
    const labels: Record<string, string> = {
      DoNothing: 'Do Nothing',
      DeactivateSubscription: 'Deactivate Subscription',
      DeleteResources: 'Delete Resources (subscription stays active)'
    };
    return labels[form.get('actionType')?.value] ?? '—';
  }

  formatQuotaAction(): string {
    const action = this.quotaLimitForm.get('actionType')?.value;
    if (!action || action === 'DoNothing') return 'Not configured';
    const amount = this.quotaLimitForm.get('additionalAmount')?.value;
    const labels: Record<string, string> = {
      DeactivateSubscription: 'Deactivate Subscription',
      DeleteResourcesAndSetReadOnly: 'Delete Resources & Set Read-Only',
      ExtendQuota: `Extend Budget by ₹${amount ?? '?'}`
    };
    return labels[action] ?? '—';
  }

  get quotaThresholdSummary(): string {
    const action = this.quotaLimitForm.get('actionType')?.value;
    if (!action || action === 'DoNothing') return 'Not configured';
    const amount = this.quotaLimitForm.get('thresholdAmount')?.value;
    return amount ? `₹${amount}` : '—';
  }

  get quotaBandsSummary(): string {
    const count = this.notificationBandsArray.length;
    return count === 0 ? 'None' : `${count} band(s) configured`;
  }

  get reviewPolicyName(): string { return this.infoForm?.get('policyName')?.value?.trim() ?? ''; }
  get reviewDescription(): string { return this.infoForm?.get('description')?.value?.trim() ?? ''; }

  openExistingPolicy(policy: PolicyTemplateSummary): void {
    this.drawerPolicy = policy;
  }

  submit(): void {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    const request: CreatePolicyTemplateRequest = {
      resourceType: 'UserAccount',
      policyName: this.reviewPolicyName,
      description: this.reviewDescription,
      uaSteps: this.buildUaSteps()
    };
    this.policyTemplateService.create(request).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.router.navigate(['/policy-template']);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg = err?.error?.message ?? 'Failed to save policy template.';
        this.snackBar.open(msg, 'Dismiss', { duration: 5000 });
      }
    });
  }
}
